<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';
import MessageList from './MessageList.vue';
import SimulationOrderPanel from './SimulationOrderPanel.vue';
import {
  PLAYGROUND_SCENARIOS,
  buildSimulationContext,
} from './playgroundSimulation';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const selectedScenarioId = ref(PLAYGROUND_SCENARIOS[0].id);
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const isOrderPanelOpen = ref(true);

const activeScenario = computed(
  () =>
    PLAYGROUND_SCENARIOS.find(
      scenario => scenario.id === selectedScenarioId.value
    ) || PLAYGROUND_SCENARIOS[0]
);

const cloneInitialMessages = () =>
  activeScenario.value.initialMessages.map(message => ({ ...message }));

const resetConversation = () => {
  messages.value = cloneInitialMessages();
  newMessage.value = '';
};

watch(
  [() => props.assistantId, selectedScenarioId],
  () => resetConversation(),
  { immediate: true }
);

const formatMessagesForApi = () =>
  messages.value.map(message => {
    const payload = {
      role: message.sender,
      content: message.content,
    };

    if (message.sender === 'assistant' && message.agentName) {
      payload.agent_name = message.agentName;
    }

    return payload;
  });

const sendMessage = async () => {
  if (!newMessage.value.trim() || isLoading.value) return;

  const currentMessage = newMessage.value.trim();
  messages.value.push({
    content: currentMessage,
    sender: 'user',
    timestamp: new Date().toISOString(),
  });
  newMessage.value = '';

  try {
    isLoading.value = true;
    const { data } = await CaptainAssistant.playground({
      assistantId: props.assistantId,
      messageContent: currentMessage,
      messageHistory: formatMessagesForApi(),
      simulationContext: JSON.stringify(
        buildSimulationContext(activeScenario.value)
      ),
    });

    messages.value.push({
      content: data.response,
      sender: 'assistant',
      agentName: data.agent_name,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting assistant response:', error);
    const errorData = error.response?.data;
    messages.value.push({
      content:
        errorData?.reasoning ||
        errorData?.response ||
        'Captain could not generate a response. Check the configured model and API access.',
      sender: 'assistant',
      timestamp: new Date().toISOString(),
      isError: true,
    });
  } finally {
    isLoading.value = false;
  }
};

const handleEnterKey = event => {
  if (event.isComposing || event.shiftKey) return;
  event.preventDefault();
  sendMessage();
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div
    class="flex h-full min-h-0 w-full overflow-hidden bg-n-surface-1 text-n-slate-11"
  >
    <aside
      class="flex w-[340px] shrink-0 flex-col bg-n-surface-1 2xl:w-[412px]"
    >
      <div
        class="flex h-12 shrink-0 items-center justify-between border-b border-n-weak px-3"
      >
        <div class="flex items-center gap-2">
          <h1 class="text-lg font-medium text-n-slate-12">会话</h1>
          <span
            class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
          >
            模拟
          </span>
        </div>
        <div class="flex items-center gap-1">
          <button
            v-for="icon in [
              'i-lucide-list-filter',
              'i-lucide-arrow-up-down',
              'i-lucide-panel-right-open',
            ]"
            :key="icon"
            type="button"
            class="flex size-8 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2"
          >
            <span class="size-4" :class="[icon]" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div
        class="flex h-10 shrink-0 items-end gap-6 border-b border-n-weak px-3 text-sm"
      >
        <button
          type="button"
          class="h-10 border-b-2 border-n-blue-9 font-medium text-n-blue-11"
        >
          场景
          <span class="ml-1 text-n-blue-10">{{
            PLAYGROUND_SCENARIOS.length
          }}</span>
        </button>
        <span class="pb-2.5 text-n-slate-9">全部为模拟数据</span>
      </div>

      <div class="flex-1 overflow-y-auto">
        <button
          v-for="scenario in PLAYGROUND_SCENARIOS"
          :key="scenario.id"
          type="button"
          class="flex min-h-[72px] w-full items-start gap-2 px-3 py-3 text-left transition-colors"
          :class="
            scenario.id === selectedScenarioId
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'hover:bg-n-alpha-1'
          "
          @click="selectedScenarioId = scenario.id"
        >
          <Avatar
            :name="scenario.contact.name || '空白对话'"
            rounded-full
            :size="32"
            class="shrink-0"
          />
          <span class="min-w-0 flex-1">
            <span class="flex items-center justify-between gap-2">
              <span class="truncate text-sm font-medium">
                {{ scenario.contact.name || '空白对话' }}
              </span>
              <span class="shrink-0 text-[11px] text-n-slate-9">
                现在 · 模拟
              </span>
            </span>
            <span class="mt-1 block truncate text-xs text-n-slate-10">
              {{ scenario.preview }}
            </span>
          </span>
        </button>
      </div>
    </aside>

    <section class="flex min-w-0 flex-1 border-l border-n-weak bg-n-surface-1">
      <div class="flex min-w-0 flex-1 flex-col bg-n-surface-1">
        <header
          class="flex h-24 shrink-0 flex-col justify-between gap-2 border-b border-n-weak px-3 py-2 xl:h-12 xl:flex-row xl:items-center"
        >
          <div class="flex min-w-0 items-center gap-2">
            <Avatar
              :name="activeScenario.contact.name || '空白对话'"
              rounded-full
              :size="32"
              class="shrink-0"
            />
            <div class="min-w-0">
              <p class="truncate text-sm font-medium text-n-slate-12">
                {{ activeScenario.contact.name || '空白对话' }}
              </p>
              <span class="block truncate text-xs text-n-slate-9">
                {{ activeScenario.subject || '无主题' }}
              </span>
            </div>
          </div>
          <div class="flex items-center justify-between gap-2">
            <label class="sr-only" for="captain-playground-scenario">
              模拟场景
            </label>
            <select
              id="captain-playground-scenario"
              v-model="selectedScenarioId"
              class="h-8 min-w-0 max-w-48 rounded-lg border border-n-weak bg-n-solid-2 px-2 text-xs text-n-slate-12"
            >
              <option
                v-for="scenario in PLAYGROUND_SCENARIOS"
                :key="scenario.id"
                :value="scenario.id"
              >
                {{ scenario.title }}
              </option>
            </select>
            <NextButton
              ghost
              sm
              slate
              icon="i-lucide-rotate-ccw"
              title="重置模拟会话"
              @click="resetConversation"
            />
            <button
              type="button"
              class="flex size-8 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2"
              title="更多"
            >
              <span class="i-lucide-ellipsis-vertical size-4" />
            </button>
          </div>
        </header>

        <div class="relative flex min-h-0 flex-1 flex-col">
          <MessageList
            :messages="messages"
            :is-loading="isLoading"
            :contact-name="activeScenario.contact.name || '模拟客户'"
          />

          <div
            class="absolute right-2 top-12 z-20 flex flex-col overflow-hidden rounded-full border border-n-weak bg-n-solid-2 shadow-sm"
          >
            <button
              v-for="tool in [
                ['i-lucide-user-round', '模拟联系人'],
                ['i-lucide-shopping-bag', '订单详情'],
                ['i-lucide-bot', 'Captain'],
              ]"
              :key="tool[1]"
              type="button"
              class="flex size-10 items-center justify-center text-n-slate-11 hover:bg-n-alpha-2"
              :class="
                tool[1] === '订单详情' && isOrderPanelOpen ? 'bg-n-alpha-2' : ''
              "
              :title="tool[1]"
              @click="
                tool[1] === '订单详情' && (isOrderPanelOpen = !isOrderPanelOpen)
              "
            >
              <span class="size-4" :class="[tool[0]]" aria-hidden="true" />
            </button>
          </div>

          <div
            class="h-[36%] min-h-[280px] max-h-[340px] shrink-0 border-t border-n-weak bg-n-surface-1 p-2 xl:h-[41%]"
          >
            <div
              class="flex h-full flex-col overflow-hidden rounded-xl border border-n-weak bg-n-surface-1"
            >
              <div class="flex items-center justify-between px-3 py-2">
                <div class="flex rounded-lg bg-n-alpha-2 p-0.5 text-xs">
                  <button
                    type="button"
                    class="rounded-md bg-n-solid-2 px-2 py-1 font-medium text-n-slate-12 shadow-sm"
                  >
                    回复
                  </button>
                  <button type="button" class="px-2 py-1 text-n-slate-11">
                    私人便笺
                  </button>
                </div>
                <div class="flex items-center gap-3 text-n-slate-10">
                  <span class="i-lucide-sparkles size-4" aria-hidden="true" />
                  <span class="i-lucide-expand size-4" aria-hidden="true" />
                </div>
              </div>

              <div
                class="flex items-center justify-between border-b border-n-weak px-3 py-2 text-xs"
              >
                <div class="min-w-0 truncate">
                  <span class="mr-3 font-medium text-n-slate-12">抄送</span>
                  <span class="text-n-slate-11">
                    {{ activeScenario.contact.email || '未设置模拟邮箱' }}
                  </span>
                </div>
                <span class="shrink-0 text-n-blue-11">模拟发送</span>
              </div>

              <textarea
                v-model="newMessage"
                class="mb-0 min-h-0 flex-1 resize-none border-0 bg-transparent px-3 py-3 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9 focus:outline-none"
                :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
                @keydown.enter.exact="handleEnterKey"
              />

              <div class="flex items-center justify-between px-3 pb-3">
                <button
                  type="button"
                  class="flex size-8 items-center justify-center rounded-lg bg-n-alpha-1 text-n-slate-10"
                  title="模拟附件"
                >
                  <span class="i-lucide-paperclip size-4" aria-hidden="true" />
                </button>
                <NextButton
                  sm
                  blue
                  :disabled="!newMessage.trim() || isLoading"
                  icon="i-lucide-send"
                  label="发送给 Captain"
                  @click="sendMessage"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <SimulationOrderPanel
        v-if="isOrderPanelOpen"
        class="flex min-w-0 max-w-[370px] flex-1 border-l border-n-weak"
        :orders="activeScenario.orders"
        @close="isOrderPanelOpen = false"
      />
    </section>
  </div>
</template>
